import React, { useEffect, useMemo, type FC } from 'react';
import { Helmet } from 'react-helmet';
import { DataHooks } from '@app/constants';
import {
  GOOGLE_ADS_GENERATOR_SCRIPT,
  GOOGLE_ADS_META_PLATFORM_ACCOUNT_CONTENT,
  GOOGLE_ADS_META_PLATFORM_ACCOUNT_NAME,
  GOOGLE_ADS_META_PLATFORM_AUTHOR_URL_NAME,
  GOOGLE_ADS_META_PLATFORM_DOMAIN_CONTENT,
  GOOGLE_ADS_META_PLATFORM_DOMAIN_NAME,
  GOOGLE_ADS_META_USER_ACCOUNT_NAME,
} from '@app/constants/google-adsense';
import { useWidgetSelector } from '@app/hooks';
import { type RootState } from '@app/store/widget/widget-store';
import { getAdUnitPublisherId } from '@app/utils';

// Global tracking for script management
let scriptLoaded = false;
let scriptLoading = false;
let widgetCounter = 0;
let currentScript: HTMLScriptElement | null = null;

const AdSenseMetaAndScript: FC = () => {
  const { adUnit } = useWidgetSelector((state: RootState) => state.widget);
  const publisherId = adUnit ? getAdUnitPublisherId(adUnit) : undefined;
  const siteUrl = useMemo(() => window.location.origin, []);

  useEffect(() => {
    if (!publisherId) {
      return;
    }

    // Increment widget counter
    widgetCounter++;

    // Check if script already exists in DOM
    const existingScript = document.querySelector(
      `[data-hook=${DataHooks.adsbygoogleScript}]`,
    ) as HTMLScriptElement;

    if (existingScript) {
      // Script already exists, just initialize ads for this widget
      scriptLoaded = true;
      currentScript = existingScript;
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (error) {
        console.error('AdSense initialization failed:', error);
      }
    } else if (!scriptLoading) {
      // Load script for the first time
      scriptLoading = true;

      const script = document.createElement('script');
      script.src = `${GOOGLE_ADS_GENERATOR_SCRIPT}?client=${publisherId}&host=${GOOGLE_ADS_META_PLATFORM_ACCOUNT_CONTENT}`;
      script.setAttribute('data-hook', DataHooks.adsbygoogleScript);
      script.async = true;

      // Handle script load event
      script.onload = () => {
        scriptLoaded = true;
        scriptLoading = false;
        currentScript = script;

        // Initialize all existing ad units on the page
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (error) {
          console.error('AdSense initialization failed:', error);
        }
      };

      // Handle script error
      script.onerror = () => {
        scriptLoading = false;
        console.error('Failed to load AdSense script');
      };

      document.body.insertBefore(script, document.body.firstChild);
    }

    // Cleanup function - decrement counter and remove script if last widget
    return () => {
      widgetCounter--;

      // Only remove script if this is the last widget and script exists
      if (widgetCounter === 0 && currentScript && currentScript.parentNode) {
        try {
          currentScript.parentNode.removeChild(currentScript);
          currentScript = null;
          scriptLoaded = false;
        } catch (error) {
          console.error('Error removing AdSense script:', error);
        }
      }
    };
  }, [publisherId]);

  const metaTags = useMemo(
    () => [
      {
        name: GOOGLE_ADS_META_PLATFORM_ACCOUNT_NAME,
        content: GOOGLE_ADS_META_PLATFORM_ACCOUNT_CONTENT,
      },
      {
        name: GOOGLE_ADS_META_PLATFORM_DOMAIN_NAME,
        content: GOOGLE_ADS_META_PLATFORM_DOMAIN_CONTENT,
      },
      {
        name: GOOGLE_ADS_META_USER_ACCOUNT_NAME,
        content: publisherId,
      },
      {
        name: GOOGLE_ADS_META_PLATFORM_AUTHOR_URL_NAME,
        content: siteUrl,
      },
    ],
    [publisherId, siteUrl],
  );

  return (
    <Helmet>
      {metaTags.map((metaTag, index) => (
        <meta key={index} {...metaTag} />
      ))}
    </Helmet>
  );
};

export default AdSenseMetaAndScript;
